import { UiNotification, UiNotificationType } from '@infuse/ui/domain';
import { useNotify as baseNotify } from '@infuse/ui/services';

/**
 * Set some defaults, so we don't have to clutter the code with
 * constantly repeating values. These functions hide much of the
 * `UiNotification` interface, because usage is almost always the
 * same.
 * <p>
 * We create a notification based on a set of defaults. Everything
 * can then be overridden by providing a `UiNotification` object with
 * desired properties. A `title` and `message` are the minimum that
 * should be provided. Only the `type` parameter is overridden with
 * a default.
 */
export const useNotify = () => {
  const n = baseNotify();

  const DEFAULTS: UiNotification = {
    type: UiNotificationType.INFO,
    group: 'notify-top',
    title: '',
    message: '',
    timeout: 5000
  };

  function notifySuccess(notification: CpmsNotification) {
    n({ ...DEFAULTS, ...notification, type: UiNotificationType.SUCCESS });
  }

  function notifyFailure(notification: CpmsNotification) {
    n({ ...DEFAULTS, ...notification, type: UiNotificationType.ERROR });
  }

  function notifyInfo(notification: CpmsNotification) {
    n({ ...DEFAULTS, ...notification, type: UiNotificationType.INFO });
  }

  function notify(notification: CpmsNotification) {
    n({ ...DEFAULTS, ...notification });
  }

  return { notifySuccess, notifyFailure, notifyInfo, notify };
};

/**
 * Keep in line with `UiNotification` field names, but
 * this provides an object with more fields that are optional.
 */
export interface CpmsNotification {
  type?: UiNotificationType;
  group?: string;
  title: string;
  message: string;
  timeout?: number;
}
